import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CURRENCY_CONTEXT_KEY = 'core829_currency';

const SUPPORTED_CURRENCIES = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
];

const CurrencyContext = createContext({
  currency: SUPPORTED_CURRENCIES[0],
  setCurrency: () => {},
  exchangeRates: {},
  lastUpdated: null,
  loading: false,
  convert: (amount) => amount,
  formatAmount: (amount) => '',
  supportedCurrencies: SUPPORTED_CURRENCIES,
});

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try {
      const saved = localStorage.getItem(CURRENCY_CONTEXT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const found = SUPPORTED_CURRENCIES.find(c => c.code === parsed.code);
        if (found) return found;
      }
    } catch {}
    return SUPPORTED_CURRENCIES[0];
  });

  const [exchangeRates, setExchangeRates] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(false);

  const setCurrency = useCallback((code) => {
    const found = SUPPORTED_CURRENCIES.find(c => c.code === code);
    if (found) {
      setCurrencyState(found);
      localStorage.setItem(CURRENCY_CONTEXT_KEY, JSON.stringify(found));
    }
  }, []);

  const fetchRates = useCallback(async () => {
    // Use cached rates if less than 1 hour old
    const now = Date.now();
    if (lastUpdated && now - lastUpdated < 60 * 60 * 1000 && Object.keys(exchangeRates).length > 0) {
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
      if (!res.ok) throw new Error('Failed to fetch rates');
      const data = await res.json();
      setExchangeRates(data.rates || {});
      setLastUpdated(now);
    } catch (e) {
      console.error('Currency fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [lastUpdated, exchangeRates]);

  useEffect(() => {
    fetchRates();
  }, []);

  const convert = useCallback((amount) => {
    if (!amount || currency.code === 'EUR') return amount;
    const rate = exchangeRates[currency.code];
    if (!rate) return amount;
    return Math.round(amount * rate * 100) / 100;
  }, [currency, exchangeRates]);

  const formatAmount = useCallback((amount) => {
    const converted = convert(amount);
    return `${currency.symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }, [convert, currency]);

  return (
    <CurrencyContext.Provider value={{
      currency,
      setCurrency,
      exchangeRates,
      lastUpdated,
      loading,
      convert,
      formatAmount,
      supportedCurrencies: SUPPORTED_CURRENCIES,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
